package app.footyguide.core.locale

/**
 * Catálogo completo de strings traduzíveis do FootyGuide.
 *
 * Uso no Compose:
 *   val strings = LocalAppStrings.current
 *   Text(strings.today)
 *
 * Adicionar novo locale:
 *   1. Criar `AppLocale.XX` em AppLocale.kt
 *   2. Adicionar instância em Strings.forLocale(locale)
 */
data class AppStrings(

    // ── Geral ────────────────────────────────────────────────
    val appName: String,
    val ok: String,
    val cancel: String,
    val back: String,
    val loading: String,
    val error: String,
    val retry: String,
    val seeAll: String,
    val close: String,
    val learnMore: String,

    // ── Navegação ────────────────────────────────────────────
    val navGames: String,
    val navFavorites: String,
    val navChannels: String,
    val navSettings: String,

    // ── Home / Jogos ─────────────────────────────────────────
    val homeTitle: String,
    val today: String,
    val tomorrow: String,
    val live: String,
    val liveLabel: String,         // "AO VIVO" chip
    val noGamesToday: String,
    val noGamesTomorrow: String,
    val noGamesForDay: String,     // "Nenhum jogo para {day}."
    val gamesCount: String,        // "{n} jogos"
    val singleGameCount: String = "1 jogo",

    // ── Detalhe do jogo ──────────────────────────────────────
    val whereToWatch: String,
    val lineup: String,
    val homeLineupLabel: String,
    val awayLineupLabel: String,
    val noLineupYet: String,
    val noChannels: String,
    val watchNow: String,
    val subscribe: String,

    // ── Favoritos ────────────────────────────────────────────
    val favoritesTitle: String,
    val favoritesSubtitle: String,
    val noFavorites: String,
    val noFavoritesHint: String,
    val upcomingGames: String,
    val noUpcomingGames: String,
    val selectedCount: String,      // "{n} selecionados"
    val favoriteTeamsCount: String, // "Times favoritos: {n}"

    // ── Canais ───────────────────────────────────────────────
    val channelsTitle: String,
    val gamesOnChannel: String,

    // ── Configurações ────────────────────────────────────────
    val settingsTitle: String,
    val settingsAccount: String,
    val settingsNotifications: String,
    val settingsAbout: String,
    val settingsVersion: String,    // "Versão {version}"
    val settingsPlan: String,
    val settingsLocale: String,
    val notifKickoff: String,
    val notifDailySummary: String,
    val notifRivals: String,

    // ── Premium ──────────────────────────────────────────────
    val premiumTitle: String,
    val premiumActive: String,
    val premiumBenefits: String,    // lista separada por \n
    val premiumThankYou: String,

    // ── Locale ───────────────────────────────────────────────
    val localePickerTitle: String,
    val localePickerSubtitle: String,
    val localeNotAvailable: String, // "Seu país ainda não está disponível. Exibindo Brasil."

    // ── Ficha do Jogo / Ações ─────────────────────────────────
    val share: String = "Compartilhar",
    val remindMe: String = "Adicionar ao Calendário",
    val matchInfo: String = "Ficha do Jogo",
    val kickoffLabel: String = "Horário",
    val roundLabel: String = "Rodada",
    val regularPhase: String = "Pontos Corridos",
    val confirmedSignals: String = "Transmissões confirmadas",
    val officialBroadcast: String = "Transmissão Oficial",
    val linkCopied: String = "Informações do jogo e link copiados!",
    val copyLinkPrompt: String = "Copie o link da partida:",
    val matchDetailsLoading: String = "Carregando detalhes da partida...",
    val matchNotFound: String = "Jogo não encontrado.",

    // ── Meu Time & Navegação ──────────────────────────────────
    val fanHub: String = "Central do Torcedor",
    val myTeamNav: String = "Meu Time",
    val changeClub: String = "Mudar de time",
    val chooseYourClub: String = "Escolha seu time",
    val overview: String = "Visão Geral",
    val calendar: String = "Calendário",
    val standings: String = "Tabela",
    val homeLabel: String = "Mandante",
    val awayLabel: String = "Visitante",
    val nextMatches: String = "Próximos Jogos",
    val recentResults: String = "Últimos Resultados",
    val continueWithoutTeam: String = "Acompanhar todos os clubes",
    val chooseClubTitle: String = "Escolha seu Clube",
    val noGamesScheduled: String = "Nenhum jogo agendado",
    val noGamesForDate: String = "Não há partidas agendadas para esta data.",
    val noGamesForTeam: String = "Nenhum jogo do seu time agendado no momento.",
    val noRecentResults: String = "Nenhum resultado recente disponível.",
    val noGamesForChannel: String = "Nenhum jogo programado para este canal hoje.",
    val nextGame: String = "Próximo jogo",
    val nextMatchHeadline: String = "Próximo Confronto",
    val myTeamStarred: String = "★ Meu Time",
    val favoriteAction: String = "+ Favoritar",
    val channelsGuide: String = "Guia de Canais",
    val todayOnTv: String = "Hoje na TV",
    val tablePointsShort: String = "P",
    val tablePlayedShort: String = "J",
    val tableWonShort: String = "V",
    val tableGoalDiffShort: String = "SG",
    val tableClub: String = "Clube",
    val pullToRefresh: String = "Puxe para atualizar",
    val releaseToRefresh: String = "Solte para atualizar",
    val releaseToHardReload: String = "Solte para recarga completa",
    val refreshing: String = "Atualizando...",
    val takingLongToRespond: String = "Demorando para responder?",
    val hardReloadAction: String = "Recarregar do zero",
    val viewGamesForDate: String = "Ver jogos de",
    val goToNextDay: String = "Ir para o próximo dia",
    val backToToday: String = "Voltar para Hoje",
    val resultWinShort: String = "V",
    val resultDrawShort: String = "E",
    val resultLossShort: String = "D",
    val roundNumberFormat: String = "{n}ª Rodada",
    val finishedLabel: String = "ENCERRADO",
    val allChannelsFilter: String = "Todos os Canais",
    val chooseClubSubtitle: String = "O FootyGuide se veste com as cores e escudo do seu time.",
    val clubQuizTitle: String = "Quiz Oficial do {club}",
    val clubQuizSubtitle: String = "Teste seus conhecimentos históricos sobre o {club} na Alefly!",
    val standingsUnavailableTitle: String = "Classificação indisponível",
    val standingsUnavailableSubtitle: String = "Ainda não há tabela consolidada para esta competição.",
    val zoneLibertadores: String = "Libertadores (G4)",
    val zonePreLibertadores: String = "Pré-Libertadores (G6)",
    val zoneSulAmericana: String = "Sul-Americana (G12)",
    val zoneRelegation: String = "Rebaixamento (Z4)",
    val localeTag: String = "pt-BR",
    val monthsShort: List<String> = listOf("jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"),
    val weekdaysShort: List<String> = listOf("SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"),
    val calendarWeekdaysHeader: List<String> = listOf("D", "S", "T", "Q", "Q", "S", "S"),
) {
    /**
     * Retorna a quantidade de jogos com suporte a singular e plural para o idioma atual.
     */
    fun formatGamesCount(count: Int): String =
        if (count == 1) singleGameCount else gamesCount.replace("{n}", count.toString())

    fun formatRoundNumber(round: Int): String =
        roundNumberFormat.replace("{n}", round.toString())
}

// ── Instâncias por locale ─────────────────────────────────────

private val PT_BR = AppStrings(
    appName = "FootyGuide",
    ok = "OK",
    cancel = "Cancelar",
    back = "Voltar",
    loading = "Carregando...",
    error = "Erro ao carregar. Tente novamente.",
    retry = "Tentar novamente",
    seeAll = "Ver todos",
    close = "Fechar",
    learnMore = "Saiba mais",

    navGames = "Jogos",
    navFavorites = "Favoritos",
    navChannels = "Canais",
    navSettings = "Configurações",

    homeTitle = "Onde assistir futebol",
    today = "Hoje",
    tomorrow = "Amanhã",
    live = "Ao Vivo",
    liveLabel = "AO VIVO",
    noGamesToday = "Nenhum jogo encontrado para hoje.",
    noGamesTomorrow = "Nenhum jogo encontrado para amanhã.",
    noGamesForDay = "Nenhum jogo para {day}.",
    gamesCount = "{n} jogos",
    singleGameCount = "1 jogo",

    whereToWatch = "📺 Onde Assistir",
    lineup = "📋 Escalação",
    homeLineupLabel = "Mandante",
    awayLineupLabel = "Visitante",
    noLineupYet = "Escalação ainda não disponível.",
    noChannels = "Transmissão a confirmar pelas emissoras",
    watchNow = "Assistir →",
    subscribe = "Assinar / Assistir agora",

    favoritesTitle = "Meus Favoritos",
    favoritesSubtitle = "Selecione seus times e competições para personalizar o app",
    noFavorites = "Descubra onde assistir futebol",
    noFavoritesHint = "Selecione seus times favoritos para uma experiência personalizada. Vamos mostrar primeiro os jogos que mais importam para você.",
    upcomingGames = "Próximos Jogos",
    noUpcomingGames = "Nenhum jogo encontrado nos próximos 7 dias.",
    selectedCount = "{n} selecionados",
    favoriteTeamsCount = "Times favoritos: {n}",

    channelsTitle = "Canais",
    gamesOnChannel = "Grade de Transmissões",

    settingsTitle = "Configurações",
    settingsAccount = "Conta",
    settingsNotifications = "Notificações",
    settingsAbout = "Sobre",
    settingsVersion = "Versão {version}",
    settingsPlan = "Plano",
    settingsLocale = "País / Mercado de transmissão",
    notifKickoff = "Alertas de início de jogo",
    notifDailySummary = "Resumo diário de jogos",
    notifRivals = "Jogo do rival",

    premiumTitle = "⭐ FootyGuide Premium",
    premiumActive = "⭐ Premium ativo",
    premiumBenefits = "• Sem anúncios\n• Notificações priorizadas\n• Suporte ao projeto",
    premiumThankYou = "Obrigado por apoiar o FootyGuide!",

    localePickerTitle = "Seu país",
    localePickerSubtitle = "Usamos isso para mostrar os canais certos para você",
    localeNotAvailable = "Seu país ainda não está disponível. Exibindo Brasil.",

    share = "Compartilhar",
    remindMe = "Adicionar ao Calendário",
    matchInfo = "Ficha do Jogo",
    kickoffLabel = "Horário",
    roundLabel = "Rodada",
    regularPhase = "Pontos Corridos",
    confirmedSignals = "Transmissões confirmadas",
    officialBroadcast = "Transmissão Oficial",
    linkCopied = "Informações do jogo e link copiados!",
    copyLinkPrompt = "Copie o link da partida:",
    matchDetailsLoading = "Carregando detalhes da partida...",
    matchNotFound = "Jogo não encontrado.",
    fanHub = "Central do Torcedor",
    myTeamNav = "Meu Time",
    changeClub = "Mudar de time",
    chooseYourClub = "Escolha seu time",
    overview = "Visão Geral",
    calendar = "Calendário",
    standings = "Tabela",
    homeLabel = "Mandante",
    awayLabel = "Visitante",
    nextMatches = "Próximos Jogos",
    recentResults = "Últimos Resultados",
    continueWithoutTeam = "Acompanhar todos os clubes",
    chooseClubTitle = "Escolha seu Clube",
)

private val EN = AppStrings(
    appName = "FootyGuide",
    ok = "OK",
    cancel = "Cancel",
    back = "Back",
    loading = "Loading...",
    error = "Failed to load. Please try again.",
    retry = "Try again",
    seeAll = "See all",
    close = "Close",
    learnMore = "Learn more",

    navGames = "Matches",
    navFavorites = "Favorites",
    navChannels = "Channels",
    navSettings = "Settings",

    homeTitle = "Where to watch football",
    today = "Today",
    tomorrow = "Tomorrow",
    live = "Live",
    liveLabel = "LIVE",
    noGamesToday = "No games found for today.",
    noGamesTomorrow = "No games found for tomorrow.",
    noGamesForDay = "No games for {day}.",
    gamesCount = "{n} games",
    singleGameCount = "1 game",

    whereToWatch = "📺 Where to Watch",
    lineup = "📋 Lineup",
    homeLineupLabel = "Home",
    awayLineupLabel = "Away",
    noLineupYet = "Lineup not available yet.",
    noChannels = "Broadcast to be confirmed",
    watchNow = "Watch now →",
    subscribe = "Subscribe / Watch now",

    favoritesTitle = "My Favorites",
    favoritesSubtitle = "Select your teams and competitions to personalize the app",
    noFavorites = "Discover where to watch football",
    noFavoritesHint = "Select your favorite teams for a personalized experience. We'll show the games that matter most to you first.",
    upcomingGames = "Upcoming Games",
    noUpcomingGames = "No games found in the next 7 days.",
    selectedCount = "{n} selected",
    favoriteTeamsCount = "Favorite teams: {n}",

    channelsTitle = "Channels",
    gamesOnChannel = "Broadcast Schedule",

    settingsTitle = "Settings",
    settingsAccount = "Account",
    settingsNotifications = "Notifications",
    settingsAbout = "About",
    settingsVersion = "Version {version}",
    settingsPlan = "Plan",
    settingsLocale = "Country / Broadcast market",
    notifKickoff = "Kickoff alerts",
    notifDailySummary = "Daily games summary",
    notifRivals = "Rival game alerts",

    premiumTitle = "⭐ FootyGuide Premium",
    premiumActive = "⭐ Premium active",
    premiumBenefits = "• No ads\n• Priority notifications\n• Support the project",
    premiumThankYou = "Thank you for supporting FootyGuide!",

    localePickerTitle = "Your country",
    localePickerSubtitle = "We use this to show you the right channels",
    localeNotAvailable = "Your country is not available yet. Showing Brazil.",

    share = "Share",
    remindMe = "Add to Calendar",
    matchInfo = "Match Details",
    kickoffLabel = "Kickoff",
    roundLabel = "Round",
    regularPhase = "League Stage",
    confirmedSignals = "Confirmed broadcasts",
    officialBroadcast = "Official Broadcast",
    linkCopied = "Match details and link copied!",
    copyLinkPrompt = "Copy match link:",
    matchDetailsLoading = "Loading match details...",
    matchNotFound = "Match not found.",
    fanHub = "Fan Central",
    myTeamNav = "My Team",
    changeClub = "Change team",
    chooseYourClub = "Choose your club",
    overview = "Overview",
    calendar = "Calendar",
    standings = "Standings",
    homeLabel = "Home",
    awayLabel = "Away",
    nextMatches = "Upcoming Matches",
    recentResults = "Recent Results",
    continueWithoutTeam = "Follow all clubs",
    chooseClubTitle = "Choose your Club",
    noGamesScheduled = "No games scheduled",
    noGamesForDate = "No matches scheduled for this date.",
    noGamesForTeam = "No upcoming games scheduled for your team.",
    noRecentResults = "No recent results available.",
    noGamesForChannel = "No games scheduled for this channel today.",
    nextGame = "Next game",
    nextMatchHeadline = "Next Match",
    myTeamStarred = "★ My Team",
    favoriteAction = "+ Favorite",
    channelsGuide = "Channels Guide",
    todayOnTv = "Today on TV",
    tablePointsShort = "PTS",
    tablePlayedShort = "P",
    tableWonShort = "W",
    tableGoalDiffShort = "GD",
    tableClub = "Club",
    pullToRefresh = "Pull to refresh",
    releaseToRefresh = "Release to refresh",
    releaseToHardReload = "Release to hard reload",
    refreshing = "Refreshing...",
    takingLongToRespond = "Taking too long?",
    hardReloadAction = "Reload app",
    viewGamesForDate = "View matches for",
    goToNextDay = "Go to next day",
    backToToday = "Back to Today",
    resultWinShort = "W",
    resultDrawShort = "D",
    resultLossShort = "L",
    roundNumberFormat = "Round {n}",
    finishedLabel = "FT",
    allChannelsFilter = "All Channels",
    chooseClubSubtitle = "FootyGuide dresses up in your club's colors and crest.",
    clubQuizTitle = "Official {club} Quiz",
    clubQuizSubtitle = "Test your historical knowledge about {club} on Alefly!",
    standingsUnavailableTitle = "Standings unavailable",
    standingsUnavailableSubtitle = "No consolidated standings available for this competition yet.",
    zoneLibertadores = "Libertadores (Top 4)",
    zonePreLibertadores = "Pre-Libertadores (Top 6)",
    zoneSulAmericana = "Sudamericana (Top 12)",
    zoneRelegation = "Relegation (Bottom 4)",
    localeTag = "en-US",
    monthsShort = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"),
    weekdaysShort = listOf("MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"),
    calendarWeekdaysHeader = listOf("S", "M", "T", "W", "T", "F", "S"),
)

private val ES = AppStrings(
    appName = "FootyGuide",
    ok = "OK",
    cancel = "Cancelar",
    back = "Volver",
    loading = "Cargando...",
    error = "Error al cargar. Intenta de nuevo.",
    retry = "Intentar de nuevo",
    seeAll = "Ver todos",
    close = "Cerrar",
    learnMore = "Más información",

    navGames = "Partidos",
    navFavorites = "Favoritos",
    navChannels = "Canales",
    navSettings = "Ajustes",

    homeTitle = "Dónde ver el fútbol",
    today = "Hoy",
    tomorrow = "Mañana",
    live = "En Vivo",
    liveLabel = "EN VIVO",
    noGamesToday = "No hay partidos para hoy.",
    noGamesTomorrow = "No hay partidos para mañana.",
    noGamesForDay = "No hay partidos para {day}.",
    gamesCount = "{n} partidos",
    singleGameCount = "1 partido",

    whereToWatch = "📺 Dónde Ver",
    lineup = "📋 Alineación",
    homeLineupLabel = "Local",
    awayLineupLabel = "Visitante",
    noLineupYet = "Alineación aún no disponible.",
    noChannels = "Transmisión por confirmar",
    watchNow = "Ver ahora →",
    subscribe = "Suscribirse / Ver ahora",

    favoritesTitle = "Mis Favoritos",
    favoritesSubtitle = "Selecciona tus equipos y competiciones",
    noFavorites = "Descubre dónde ver el fútbol",
    noFavoritesHint = "Selecciona tus equipos favoritos para una experiencia personalizada.",
    upcomingGames = "Próximos Partidos",
    noUpcomingGames = "No hay partidos en los próximos 7 días.",
    selectedCount = "{n} seleccionados",
    favoriteTeamsCount = "Equipos favoritos: {n}",

    channelsTitle = "Canales",
    gamesOnChannel = "Programación del Canal",

    settingsTitle = "Ajustes",
    settingsAccount = "Cuenta",
    settingsNotifications = "Notificaciones",
    settingsAbout = "Acerca de",
    settingsVersion = "Versión {version}",
    settingsPlan = "Plan",
    settingsLocale = "País / Mercado de transmisión",
    notifKickoff = "Alertas de inicio de partido",
    notifDailySummary = "Resumen diario de partidos",
    notifRivals = "Partido del rival",

    premiumTitle = "⭐ FootyGuide Premium",
    premiumActive = "⭐ Premium activo",
    premiumBenefits = "• Sin publicidad\n• Notificaciones prioritarias\n• Apoya el proyecto",
    premiumThankYou = "¡Gracias por apoyar FootyGuide!",

    localePickerTitle = "Tu país",
    localePickerSubtitle = "Lo usamos para mostrarte los canales correctos",
    localeNotAvailable = "Tu país aún no está disponible. Mostrando Brasil.",

    share = "Compartir",
    remindMe = "Añadir al Calendario",
    matchInfo = "Ficha del Partido",
    kickoffLabel = "Horario",
    roundLabel = "Jornada",
    regularPhase = "Fase Regular",
    confirmedSignals = "Transmisiones confirmadas",
    officialBroadcast = "Transmisión Oficial",
    linkCopied = "¡Detalles del partido y enlace copiados!",
    copyLinkPrompt = "Copia el enlace del partido:",
    matchDetailsLoading = "Cargando detalles del partido...",
    matchNotFound = "Partido no encontrado.",
    fanHub = "Zona del Hincha",
    myTeamNav = "Mi Equipo",
    changeClub = "Cambiar equipo",
    chooseYourClub = "Elige tu club",
    overview = "Resumen",
    calendar = "Calendario",
    standings = "Clasificación",
    homeLabel = "Local",
    awayLabel = "Visitante",
    nextMatches = "Próximos Partidos",
    recentResults = "Últimos Resultados",
    continueWithoutTeam = "Seguir todos los clubes",
    chooseClubTitle = "Elige tu Club",
    noGamesScheduled = "No hay partidos programados",
    noGamesForDate = "No hay partidos programados para esta fecha.",
    noGamesForTeam = "No hay próximos partidos de tu equipo programados.",
    noRecentResults = "No hay resultados recientes disponibles.",
    noGamesForChannel = "No hay partidos programados para este canal hoy.",
    nextGame = "Próximo partido",
    nextMatchHeadline = "Próximo Partido",
    myTeamStarred = "★ Mi Equipo",
    favoriteAction = "+ Favorito",
    channelsGuide = "Guía de Canales",
    todayOnTv = "Hoy en TV",
    tablePointsShort = "P",
    tablePlayedShort = "PJ",
    tableWonShort = "PG",
    tableGoalDiffShort = "DG",
    tableClub = "Club",
    pullToRefresh = "Desliza para actualizar",
    releaseToRefresh = "Suelta para actualizar",
    releaseToHardReload = "Suelta para recarga completa",
    refreshing = "Actualizando...",
    takingLongToRespond = "¿Tardando en responder?",
    hardReloadAction = "Recargar de cero",
    viewGamesForDate = "Ver partidos de",
    goToNextDay = "Ir al día siguiente",
    backToToday = "Volver a Hoy",
    resultWinShort = "V",
    resultDrawShort = "E",
    resultLossShort = "D",
    roundNumberFormat = "{n}ª Jornada",
    finishedLabel = "FINALIZADO",
    allChannelsFilter = "Todos los Canales",
    chooseClubSubtitle = "FootyGuide se viste con los colores y el escudo de tu equipo.",
    clubQuizTitle = "Quiz Oficial de {club}",
    clubQuizSubtitle = "¡Pon a prueba tus conocimientos históricos sobre {club} en Alefly!",
    standingsUnavailableTitle = "Clasificación no disponible",
    standingsUnavailableSubtitle = "Aún no hay tabla consolidada para esta competición.",
    zoneLibertadores = "Libertadores (G4)",
    zonePreLibertadores = "Pre-Libertadores (G6)",
    zoneSulAmericana = "Sudamericana (G12)",
    zoneRelegation = "Descenso (Z4)",
    localeTag = "es-ES",
    monthsShort = listOf("ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"),
    weekdaysShort = listOf("LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"),
    calendarWeekdaysHeader = listOf("D", "L", "M", "X", "J", "V", "S"),
)

// ── API pública ───────────────────────────────────────────────

/**
 * Retorna as strings para um idioma da UI.
 *
 * Separado do locale de transmissão — um brasileiro nos EUA tem
 * locale = "US" (canais americanos) mas language = PT (UI em português).
 */
object Strings {
    fun forLanguage(language: UiLanguage): AppStrings = when (language) {
        UiLanguage.PT -> PT_BR
        UiLanguage.EN -> EN
        UiLanguage.ES -> ES
        UiLanguage.FR, UiLanguage.DE, UiLanguage.IT -> EN // fallback EN até tradução
    }

    /** Atalho por código do idioma ("PT", "EN", "ES"...). */
    fun forLanguageCode(code: String): AppStrings =
        forLanguage(UiLanguage.fromCode(code))
}

// ── Helpers de interpolação ───────────────────────────────────

/** Substitui `{day}` pelo valor fornecido. */
fun String.withDay(day: String): String = replace("{day}", day)

/** Substitui `{n}` pelo valor fornecido. */
fun String.withCount(n: Int): String = replace("{n}", n.toString())

/** Substitui `{version}` pelo valor fornecido. */
fun String.withVersion(v: String): String = replace("{version}", v)
